import React, { useState } from 'react';

import type { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';

import { Alert, AlertTitle, Box, Link, Stack, Typography } from '@mui/material';

import TaxBreakdownDialog from './TaxBreakdownDialog';
import { uiSchema } from './formData';
import type { TaxFeesFormData } from './formData';
import type { useTaxesFeesStep } from './useTaxesFeesStep';

type Props = {
  disabled?: boolean;
  formId: string;
  onSaved: () => void | Promise<void>;
  propertyId?: string;
  taxesFeesStep: ReturnType<typeof useTaxesFeesStep>;
};

function TaxesFeesStep({ disabled = false, formId, onSaved, propertyId, taxesFeesStep }: Props) {
  const [isTaxBreakdownOpen, setIsTaxBreakdownOpen] = useState(false);
  const { errorMessage, formData, handleChange, isLoading, prefillChanges, saveDraft, schema } = taxesFeesStep;

  if (!propertyId) {
    return <Alert severity="warning">Select or create a property before editing taxes and fees.</Alert>;
  }

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  async function handleSubmit({ formData: submittedFormData }: IChangeEvent<TaxFeesFormData>) {
    if (!submittedFormData) {
      return;
    }

    saveDraft(submittedFormData);
    await onSaved();
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Please note:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          <Typography component="li" variant="body2">
            Taxes/fees can be per night or per stay
          </Typography>
          <Typography component="li" variant="body2">
            Each entry will be applied to the gross price
          </Typography>
          <Typography component="li" variant="body2">
            Ordering does not affect the final calculation
          </Typography>
          <Typography component="li" variant="body2">
            Channel manager taxes & fees will take precedence over property taxes & fees
          </Typography>
          <Typography component="li" variant="body2">
            Taxes marked as "Channel Manager Additional Tax" will be added to the rates sent by the channel manager
          </Typography>
          <Typography component="li" variant="body2">
            "Channel Manager Additional Tax" taxes that are also marked as "Send Tax-Exclusive Rates to CM" will be
            exclude from the rates sent to the channel manager when a reservation is made
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary">
        Tax information source:{' '}
        <Link component="button" type="button" onClick={() => setIsTaxBreakdownOpen(true)}>
          Tax Breakdown by Country
        </Link>
      </Typography>

      <TaxBreakdownDialog open={isTaxBreakdownOpen} onClose={() => setIsTaxBreakdownOpen(false)} />

      {!!(prefillChanges?.added.length || prefillChanges?.changed.length || prefillChanges?.removed.length) && (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Taxes & fees synced with this location's rules</AlertTitle>
          <Typography variant="body2">Review before saving — nothing is stored until you save this step.</Typography>
          {(
            [
              ['Added', prefillChanges.added],
              ['Changed', prefillChanges.changed],
              ['Removed', prefillChanges.removed],
            ] as const
          )
            .filter(([, entries]) => entries.length)
            .map(([label, entries]) => (
              <Box key={label} sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {label}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                  {entries.map((entry) => (
                    <Typography component="li" variant="body2" key={entry}>
                      {entry}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
        </Alert>
      )}

      {schema && formData && (
        <Form
          id={formId}
          schema={schema}
          uiSchema={uiSchema}
          formData={formData}
          disabled={disabled}
          onChange={({ formData: nextFormData }: IChangeEvent<TaxFeesFormData>) =>
            nextFormData && handleChange(nextFormData)
          }
          onSubmit={handleSubmit}
          validator={validator}
        >
          {/* Empty child suppresses RJSF's default submit button; the wizard owns the save control. */}
          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
          <></>
        </Form>
      )}

      {!!errorMessage && <Alert severity="error">{errorMessage}</Alert>}
    </Stack>
  );
}

export default TaxesFeesStep;
